import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Address, formatUnits } from 'viem';
import { useConnection, useReadContract } from 'wagmi';
import { mainnet } from 'wagmi/chains';

import AnimeLoader from '@/app/assets/svg/anime-loader.svg?react';
import ChevronRightIcon from '@/app/assets/svg/chevron-right.svg?react';
import WalletIcon from '@/app/assets/svg/wallet.svg?react';
import WyldsIcon from '@/app/assets/token/wylds.png';
import { TokenSelectItem } from '@/app/components/Modal/TokenRow';
import { TokenSelectDropdown } from '@/app/components/Modal/TokenSelectDropdown';
import { TokenSelectList } from '@/app/components/Modal/TokenSelectList';
import DepositSwapDetailsForm from '@/app/components/VaultsItemPageComponents/DepositSwapDetailsForm';
import { useVaultActionDetails } from '@/app/components/VaultsItemPageComponents/hooks/useVaultActionDetails';
import { useVaultDeposit } from '@/app/components/VaultsItemPageComponents/hooks/useVaultDeposit';
import { useVaultDepositSequential } from '@/app/components/VaultsItemPageComponents/hooks/useVaultDepositSequential';
import { useVaultTokenSymbols } from '@/app/components/VaultsItemPageComponents/hooks/useVaultTokenSymbols';
import { useVaultWithdraw } from '@/app/components/VaultsItemPageComponents/hooks/useVaultWithdraw';
import { useVaultWithdrawSequential } from '@/app/components/VaultsItemPageComponents/hooks/useVaultWithdrawSequential';
import { useVaultById } from '@/app/components/VaultsPageComponents/hooks/useVaults';
import { DASH, TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { StepModalTextType, TransactionCompletedModalTextType } from '@/app/shared/constants/modals';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { TOKENS } from '@/app/shared/constants/tokens';
import { VAULT_TRANCHE, VAULT_TRANCHE_LABEL, VAULT_TRANCHES, VaultTrancheId } from '@/app/shared/constants/vaults';
import { ABI, USDC, WYLDS } from '@/app/shared/constants/web3/constants';
import { useBalances } from '@/app/shared/hooks/blockchain/useBalances';
import { useCheckBatching } from '@/app/shared/hooks/blockchain/useCheckBatching';
import { useIsWhitelisted } from '@/app/shared/hooks/blockchain/useIsWhitelisted';
import { useOnClickOutside } from '@/app/shared/hooks/useOnClickOutside';
import { useScreenWidth } from '@/app/shared/hooks/useScreenWidth';
import { cn } from '@/app/shared/lib/cn';
import { formatNumber, formatNumberInputValue, isValidAmountInput } from '@/app/shared/lib/formatters';
import { getStepStatus } from '@/app/shared/lib/getStepStatus';
import { preloadModals } from '@/app/shared/lib/preloadModals';
import { formatVaultApy } from '@/app/shared/lib/vaultFormatters';
import { Button } from '@/app/shared/ui/Button';
import { Drawer } from '@/app/shared/ui/Drawer';
import { Input } from '@/app/shared/ui/Input';
import { Tabs } from '@/app/shared/ui/Tabs';

const TOKEN_SELECT_DRAWER_MAX_WIDTH = 540;

const StepModal = lazy(() => import('@/app/components/Modal/StepModal'));
const SequentialStepModal = lazy(() => import('@/app/components/Modal/SequentialStepModal'));
const TransactionCompleted = lazy(() => import('@/app/components/Modal/TransactionCompleted'));

type DepositTokenOption = TokenSelectItem & { decimals: number };

const MAINNET_SWAP_DEPOSIT_TOKENS: DepositTokenOption[] = [
  {
    address: WYLDS,
    symbol: 'wYLDS',
    name: 'Hastra wYLDS',
    logoURI: WyldsIcon,
    decimals: 6,
  },
  {
    address: TOKENS[USDC].address,
    symbol: TOKENS[USDC].symbol,
    name: 'USD Coin',
    Icon: TOKENS[USDC].Icon,
    decimals: TOKENS[USDC].decimals,
  },
];

const assetDepositToken = (asset: Address): DepositTokenOption => {
  const known = MAINNET_SWAP_DEPOSIT_TOKENS.find(
    (token) => token.address.toLowerCase() === asset.toLowerCase(),
  );
  if (known) return { ...known, address: asset };

  return {
    address: asset,
    symbol: TOKENS[USDC].symbol,
    name: 'USD Coin',
    Icon: TOKENS[USDC].Icon,
    decimals: TOKENS[USDC].decimals,
  };
};

interface VaultsItemActionCardProps {
  vaultId?: string;
  selectedTrancheId: VaultTrancheId;
  onTrancheChange: (id: VaultTrancheId) => void;
}

const ACTION_TABS = [
  { id: 'deposit' as const, content: 'Deposit' },
  { id: 'withdraw' as const, content: 'Withdraw' },
];

const getCompletedTextType = (trancheId: VaultTrancheId): TransactionCompletedModalTextType => {
  if (trancheId === VAULT_TRANCHE.JUNIOR) return TransactionCompletedModalTextType.JUNIOR;
  return TransactionCompletedModalTextType.SENIOR;
};

const VaultsItemActionCard = ({ vaultId, selectedTrancheId, onTrancheChange }: VaultsItemActionCardProps) => {
  const [action, setAction] = useState<'deposit' | 'withdraw'>('deposit');
  const [amount, setAmount] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [selectedDepositToken, setSelectedDepositToken] = useState(MAINNET_SWAP_DEPOSIT_TOKENS[0]);
  const [showSwapDetails, setShowSwapDetails] = useState(false);
  const tokenSelectRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { address, isConnected, chainId } = useConnection();
  const screenWidth = useScreenWidth();
  const isTokenSelectDrawer = screenWidth > 0 && screenWidth < TOKEN_SELECT_DRAWER_MAX_WIDTH;
  useOnClickOutside(tokenSelectRef, () => {
    if (!isTokenSelectDrawer) setIsTokenSelectOpen(false);
  });
  const { isWhitelisted } = useIsWhitelisted(address as Address | undefined);
  const { vault } = useVaultById(vaultId);
  const supportsBatching = useCheckBatching();

  // DefiLlama / 0x swap is mainnet-only — hide when wallet is on testnet.
  const isSwapAvailable = chainId === mainnet.id;

  const isDeposit = action === 'deposit';
  const useSequentialDeposit = isDeposit && isConnected && !supportsBatching;
  const useSequentialWithdraw = !isDeposit && isConnected && !supportsBatching;
  const useSequentialFlow = useSequentialDeposit || useSequentialWithdraw;

  const deposit = useVaultDeposit({
    amount,
    trancheId: selectedTrancheId,
    vaultId,
    enabled: isDeposit && supportsBatching,
  });
  const depositSequential = useVaultDepositSequential({
    amount,
    trancheId: selectedTrancheId,
    vaultId,
    trancheSymbol: VAULT_TRANCHE_LABEL[selectedTrancheId],
    enabled: useSequentialDeposit,
  });
  const withdraw = useVaultWithdraw({
    amount,
    trancheId: selectedTrancheId,
    vaultId,
    enabled: !isDeposit && supportsBatching,
  });
  const withdrawSequential = useVaultWithdrawSequential({
    amount,
    trancheId: selectedTrancheId,
    vaultId,
    trancheSymbol: VAULT_TRANCHE_LABEL[selectedTrancheId],
    enabled: useSequentialWithdraw,
  });

  const activeDeposit = useSequentialDeposit ? depositSequential : deposit;
  const activeWithdraw = useSequentialWithdraw ? withdrawSequential : withdraw;
  const activeFlow = isDeposit ? activeDeposit : activeWithdraw;
  const {
    handleSend,
    transactionStatusData,
    txHash,
    errorMessage,
    isBatchingTransaction,
    resetTransactionState,
    trancheAddress,
    contracts,
    debouncedAmount,
    isReady,
  } = activeFlow;

  const amountUsdc = isDeposit ? activeDeposit.amountUsdc : undefined;
  const amountMzusd = isDeposit ? activeDeposit.amountMzusd : undefined;
  const sharesAmount = !isDeposit ? activeWithdraw.sharesAmount : undefined;
  const previewAssets = !isDeposit ? activeWithdraw.previewAssets : undefined;
  const isInstantWithdraw = !isDeposit ? activeWithdraw.isInstantWithdraw : false;

  const depositTokens = useMemo((): DepositTokenOption[] => {
    if (isSwapAvailable) return MAINNET_SWAP_DEPOSIT_TOKENS;
    if (contracts?.ASSET) return [assetDepositToken(contracts.ASSET)];
    return [];
  }, [contracts?.ASSET, isSwapAvailable]);

  const canSelectDepositToken = depositTokens.length > 1;

  const isUsdcDeposit =
    isDeposit &&
    isSwapAvailable &&
    selectedDepositToken.address.toLowerCase() === USDC.toLowerCase();

  const { usdcSymbol, mzusdSymbol, trancheSymbol } = useVaultTokenSymbols({
    contracts: activeDeposit.contracts ?? activeWithdraw.contracts,
    trancheAddress,
  });

  const assetSymbol = usdcSymbol ?? 'wYLDS';
  const trancheTokenSymbol = trancheSymbol ?? VAULT_TRANCHE_LABEL[selectedTrancheId];
  const mzusdTokenSymbol = mzusdSymbol ?? 'mzUSD';

  const {
    yourApy,
    unlockPeriod,
    depositRoute,
    depositFee,
    withdrawFee,
    estimatedAvailability,
  } = useVaultActionDetails({
    vaultId,
    trancheId: selectedTrancheId,
    trancheSymbol: trancheTokenSymbol,
    assetSymbol,
    mzusdSymbol: mzusdTokenSymbol,
    isUsdcDeposit,
  });

  useEffect(() => {
    if (depositTokens.length === 0) return;

    const isSelectedAvailable = depositTokens.some(
      (token) => token.address.toLowerCase() === selectedDepositToken.address.toLowerCase(),
    );
    if (isSelectedAvailable) return;

    setSelectedDepositToken(depositTokens[0]);
    setAmount('');
    setShowSwapDetails(false);
    setIsTokenSelectOpen(false);
  }, [depositTokens, selectedDepositToken.address]);

  useEffect(() => {
    if (isSwapAvailable) return;
    setShowSwapDetails(false);
  }, [isSwapAvailable]);

  const getTrancheApy = (trancheId: VaultTrancheId) => {
    if (!vault) return DASH;
    if (trancheId === VAULT_TRANCHE.SENIOR) return formatVaultApy(vault.trancheSeniorAPY);
    if (trancheId === VAULT_TRANCHE.JUNIOR) return formatVaultApy(vault.trancheJuniorAPY);
    return DASH;
  };

  const { data: withdrawPreviewAssets } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'convertToAssets',
    args: sharesAmount && sharesAmount > 0n ? [sharesAmount] : [0n],
    query: {
      enabled: !isDeposit && !!trancheAddress && !!sharesAmount && sharesAmount > 0n,
    },
  });

  const selectedDepositAddress = (
    isUsdcDeposit && contracts ? contracts.USDC : selectedDepositToken.address
  ) as Address;

  const balanceTokens =
    contracts && trancheAddress
      ? isDeposit
        ? Array.from(
            new Set(
              [
                contracts.ASSET,
                ...depositTokens.map((token) => token.address as Address),
              ].map((token) => token.toLowerCase() as Address),
            ),
          )
        : [trancheAddress]
      : [];

  const { data: balances, refetch: refetchBalances } = useBalances({
    tokens: balanceTokens,
    address,
    enabled: !!contracts && isConnected && balanceTokens.length > 0,
  });

  const depositTokenBalance = balances?.[selectedDepositAddress.toLowerCase()] ?? 0n;
  const sharesBalance = trancheAddress
    ? (balances?.[trancheAddress.toLowerCase()] ?? 0n)
    : 0n;
  const depositTokenBalanceString = formatUnits(depositTokenBalance, selectedDepositToken.decimals);
  const sharesBalanceString = formatUnits(sharesBalance, TRANCHE_SHARE_DECIMALS);
  const walletBalanceLabel = isDeposit
    ? formatNumber(depositTokenBalanceString, 2, 2)
    : formatNumber(sharesBalanceString, 2, 2);

  const { data: previewShares } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'previewDeposit',
    args: amountMzusd && amountMzusd > 0n ? [amountMzusd] : [0n],
    query: {
      enabled: isDeposit && !!trancheAddress && !!amountMzusd && amountMzusd > 0n,
    },
  });

  const oneShare = 10n ** BigInt(TRANCHE_SHARE_DECIMALS);
  const { data: oneShareAssets } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'convertToAssets',
    args: [oneShare],
    query: {
      enabled: !!trancheAddress,
    },
  });

  const { isSuccess: isTxSuccess, isLoading: isProcessing } = transactionStatusData;
  const transactionStatus = getStepStatus(transactionStatusData);

  const isInsufficientUsdc = (() => {
    if (!isDeposit || !amountUsdc || amountUsdc <= 0n) return false;
    if (useSequentialDeposit) {
      return depositSequential.needUsdc > 0n && depositTokenBalance < depositSequential.needUsdc;
    }
    return depositTokenBalance < amountUsdc;
  })();
  const isInsufficientShares =
    !isDeposit && sharesAmount !== undefined && sharesAmount > 0n && sharesBalance < sharesAmount;

  useEffect(() => {
    if (!isTxSuccess) return;

    refetchBalances();
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_POSITIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_PORTFOLIO] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_TRANSACTIONS] });
    if (!isDeposit) {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_PENDING_WITHDRAWALS] });
    }

    const timer = setTimeout(() => {
      setIsModalOpen(false);
      setIsSuccessModalOpen(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [isDeposit, isTxSuccess, queryClient, refetchBalances]);

  useEffect(() => {
    if (isConnected) return;
    setAmount('');
    setShowSwapDetails(false);
    setIsModalOpen(false);
    setIsSuccessModalOpen(false);
    setIsTokenSelectOpen(false);
  }, [isConnected]);

  const handleSwapDetailsBack = useCallback(() => {
    setShowSwapDetails(false);
  }, []);

  const handleAction = () => {
    if (!isValidAmountInput(amount) || !isWhitelisted) return;
    if (isInsufficientUsdc || isInsufficientShares) return;
    if (isUsdcDeposit) {
      setShowSwapDetails(true);
      return;
    }
    if (!isReady) return;
    preloadModals();
    handleSend();
    setIsModalOpen(true);
  };

  const handleMaxClick = () =>
    setAmount(isDeposit ? depositTokenBalanceString : sharesBalanceString);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxDecimals = isDeposit ? selectedDepositToken.decimals : TRANCHE_SHARE_DECIMALS;
    setAmount((prev) => formatNumberInputValue(event.target.value, prev, maxDecimals));
  };

  const handleTabChange = (tab: 'deposit' | 'withdraw') => {
    setAction(tab);
    setAmount('');
    setShowSwapDetails(false);
    setIsTokenSelectOpen(false);
    deposit.resetTransactionState();
    depositSequential.resetTransactionState();
    withdraw.resetTransactionState();
    withdrawSequential.resetTransactionState();
  };

  const handleTrancheChange = (id: VaultTrancheId) => {
    onTrancheChange(id);
    setAmount('');
    setShowSwapDetails(false);
    setIsTokenSelectOpen(false);
    deposit.resetTransactionState();
    depositSequential.resetTransactionState();
    withdraw.resetTransactionState();
    withdrawSequential.resetTransactionState();
  };

  const handleCloseTransactionCompleteModal = () => {
    resetTransactionState();
    setAmount('');
    setIsSuccessModalOpen(false);
  };

  const handleSequentialRetry = () => {
    if (useSequentialDeposit) {
      void depositSequential.handleSend();
      return;
    }
    if (useSequentialWithdraw) {
      void withdrawSequential.handleSend();
    }
  };

  const sequentialSteps = useSequentialDeposit
    ? depositSequential.steps
    : useSequentialWithdraw
      ? withdrawSequential.steps
      : [];
  const sequentialTitle = isDeposit ? 'Deposit' : 'Withdraw';

  const previewSharesFormatted =
    previewShares !== undefined ? Number(formatUnits(previewShares, TRANCHE_SHARE_DECIMALS)).toFixed(4) : undefined;

  const previewAssetsFormatted =
    withdrawPreviewAssets !== undefined
      ? Number(formatUnits(withdrawPreviewAssets, 18)).toFixed(4)
      : undefined;

  const sharePriceUsdc =
    oneShareAssets !== undefined ? Number(formatUnits(oneShareAssets, 18)).toFixed(4) : undefined;

  type DetailRow = {
    label: string;
    value: string;
    valueClassName?: string;
    wrapValue?: boolean;
  };

  const details: DetailRow[] = isDeposit
    ? [
        { label: 'Your APY', value: yourApy, valueClassName: 'text-apy-value' },
        { label: 'Unlock period', value: unlockPeriod },
        { label: 'Deposit route', value: depositRoute, wrapValue: true },
        { label: 'Fee', value: depositFee },
      ]
    : [
        { label: 'Your APY', value: yourApy, valueClassName: 'text-apy-value' },
        { label: 'Unlock period', value: unlockPeriod },
        { label: 'Fee', value: withdrawFee },
        { label: 'Estimated availability', value: estimatedAvailability },
      ];

  const getButtonLabel = (connected: boolean) => {
    if (!connected) return 'Connect Wallet';
    if (!contracts) return vaultId ? 'Vault Unavailable' : 'Unsupported Network';
    if (!trancheAddress) return 'Tranche Unavailable';
    if (isInsufficientUsdc) return `Insufficient ${selectedDepositToken.symbol}`;
    if (isInsufficientShares) return `Insufficient ${trancheTokenSymbol}`;
    if (!isWhitelisted) return 'KYC Required';
    if (isProcessing) return 'Processing...';
    if (isUsdcDeposit) return 'Swap';
    return isDeposit ? 'Deposit' : 'Withdraw';
  };

  const hasValidAmount = isValidAmountInput(amount);

  const isSwapActionDisabled =
    !hasValidAmount || !isWhitelisted || isInsufficientUsdc || isProcessing;

  const isDepositActionDisabled =
    !isReady ||
    !hasValidAmount ||
    !isWhitelisted ||
    isInsufficientUsdc ||
    isInsufficientShares ||
    isProcessing;

  const isActionDisabled = isUsdcDeposit ? isSwapActionDisabled : isDepositActionDisabled;

  const youWillGetLabel = (() => {
    if (isDeposit) {
      return previewSharesFormatted
        ? `~${Number(previewSharesFormatted).toFixed(2)} ${trancheTokenSymbol}`
        : '-';
    }
    if (isInstantWithdraw) {
      return previewAssets && sharesAmount && sharesAmount > 0n ? `~${previewAssetsFormatted} ${mzusdTokenSymbol}` : '-';
    }
    return withdrawPreviewAssets && sharesAmount && sharesAmount > 0n
      ? `~${previewAssetsFormatted} ${mzusdTokenSymbol}`
      : '-';
  })();

  const exchangeRateLabel = `1 ${trancheTokenSymbol} = ${sharePriceUsdc ?? 'X.XXXX'} ${assetSymbol}`;
  const SelectedDepositTokenIcon = selectedDepositToken.Icon;

  const renderSelectedDepositTokenIcon = () => {
    if (SelectedDepositTokenIcon) {
      return <SelectedDepositTokenIcon className='h-8 w-8 rounded-full' aria-hidden />;
    }
    if (selectedDepositToken.logoURI) {
      return (
        <img
          src={selectedDepositToken.logoURI}
          alt=''
          className='h-8 w-8 rounded-full object-cover'
          aria-hidden
        />
      );
    }
    return <span className='h-8 w-8 rounded-full bg-gray-800' aria-hidden />;
  };

  return (
    <>
      <div className='flex h-auto w-full min-w-0 flex-col gap-5 rounded-md bg-elevated-card-2 p-5 min-[1056px]:min-h-[622px] min-[1056px]:w-[473px] min-[1056px]:shrink-0'>
        {showSwapDetails ? (
          <DepositSwapDetailsForm
            usdcAmount={amount}
            trancheId={selectedTrancheId}
            vaultId={vaultId}
            vaultName={vault?.name}
            assetSymbol={assetSymbol}
            trancheSymbol={trancheTokenSymbol}
            onBack={handleSwapDetailsBack}
            onFlowComplete={() => {
              setShowSwapDetails(false);
              setAmount('');
              queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_POSITIONS] });
              queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_PORTFOLIO] });
              queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_TRANSACTIONS] });
              void refetchBalances();
            }}
          />
        ) : (
          <>
            <Tabs
              tabs={ACTION_TABS}
              activeTab={action}
              onTabChange={handleTabChange}
              className='flex h-9.5 w-full rounded-md bg-background p-0.5'
              tabClassName='flex h-full flex-1 items-center justify-center rounded-sm text-base leading-5'
              activeTabClassName='bg-elevated-card-2 text-brand-1100'
              inactiveTabClassName='text-gray-400 hover:text-gray-50'
            />

            <div className='grid grid-cols-2'>
              {VAULT_TRANCHES.map((tranche) => {
                const isActive = tranche.id === selectedTrancheId;

                return (
                  <button
                    key={tranche.id}
                    type='button'
                    onClick={() => handleTrancheChange(tranche.id)}
                    className={cn(
                      'flex h-25 flex-col items-start justify-center border-b-2 px-5 text-left',
                      isActive
                        ? 'border-brand-1100'
                        : 'border-decoration-stroke [:root.light_&]:border-gray-900',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-9.5 items-center text-base',
                        isActive ? 'text-gray-200' : 'text-gray-400',
                      )}
                    >
                      {VAULT_TRANCHE_LABEL[tranche.id]}
                    </span>
                    <span className={cn('text-2xl leading-none', isActive ? 'text-brand-1100' : 'text-gray-400')}>
                      {getTrancheApy(tranche.id)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className='flex flex-col gap-2 border-b border-decoration-stroke pb-5 [:root.light_&]:border-gray-900'>
              <div className='flex items-center justify-between text-base'>
                <span className='text-brand-1100'>{isDeposit ? 'You deposit' : 'You withdraw'}</span>
                <span className='flex items-center gap-1.5 text-base text-gray-400'>
                  <WalletIcon className='h-4 w-4' />
                  {isConnected && contracts ? walletBalanceLabel : '0.0'}
                </span>
              </div>
              <div ref={tokenSelectRef} className='relative'>
                <div
                  className={cn(
                    'flex h-17 items-center gap-3 rounded-md border bg-transparent px-4 [:root.light_&]:focus-within:border-gray-700',
                    isTokenSelectOpen
                      ? 'border-modal-result-body [:root.light_&]:border-gray-900'
                      : 'border-gray-700 [:root.light_&]:border-gray-900',
                  )}
                >
                  {isDeposit && (
                    <button
                      type='button'
                      onClick={() => {
                        if (!canSelectDepositToken) return;
                        setIsTokenSelectOpen((open) => !open);
                      }}
                      disabled={!isConnected || isProcessing || !canSelectDepositToken}
                      className='flex shrink-0 items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50'
                      aria-label='Select token'
                      aria-expanded={isTokenSelectOpen}
                      aria-haspopup='listbox'
                    >
                      {renderSelectedDepositTokenIcon()}
                      {canSelectDepositToken && (
                        <ChevronRightIcon
                          className={cn(
                            'h-3.5 w-3.5 text-gray-400 transition-transform',
                            isTokenSelectOpen ? '-rotate-90' : 'rotate-90',
                          )}
                          aria-hidden
                        />
                      )}
                    </button>
                  )}
                  <Input
                    type='text'
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder='0.00'
                    disabled={!isConnected || isProcessing}
                    className='h-full w-full border-none bg-transparent p-0 text-xl text-gray-200 placeholder:text-gray-500 focus:outline-none'
                  />
                  <button
                    type='button'
                    onClick={handleMaxClick}
                    disabled={!isConnected || !contracts}
                    className='h-9 shrink-0 rounded-sm bg-gray-900 px-3 text-sm text-brand-1100 disabled:opacity-50'
                  >
                    MAX
                  </button>
                </div>
                {isDeposit && canSelectDepositToken && isTokenSelectOpen && !isTokenSelectDrawer && (
                  <TokenSelectDropdown
                    tokens={depositTokens}
                    selectedToken={selectedDepositToken.address}
                    onSelect={(token) => {
                      const next =
                        depositTokens.find(
                          (item) => item.address.toLowerCase() === token.address.toLowerCase(),
                        ) ?? depositTokens[0];
                      setSelectedDepositToken(next);
                      setAmount('');
                      setShowSwapDetails(false);
                      setIsTokenSelectOpen(false);
                    }}
                  />
                )}
              </div>
              <div className='flex items-start justify-between gap-3 text-sm text-gray-400 max-[500px]:flex-col max-[500px]:items-start max-[500px]:gap-1'>
                <p>You will get: {youWillGetLabel}</p>
                <p className='text-right max-[500px]:text-left'>{exchangeRateLabel}</p>
              </div>
            </div>

            <dl className='flex flex-col'>
              {details.map((row) => (
                <div
                  key={row.label}
                  className={cn(
                    'flex gap-1.5 px-4 py-2 text-sm',
                    row.wrapValue
                      ? 'flex-col items-start min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between'
                      : 'items-center justify-between',
                  )}
                >
                  <dt className='shrink-0 text-gray-200'>{row.label}</dt>
                  <dd
                    className={cn(
                      'min-w-0',
                      row.wrapValue
                        ? 'break-words text-left min-[600px]:text-right'
                        : 'text-right whitespace-nowrap',
                      row.valueClassName ?? 'text-gray-200',
                    )}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>

            <ConnectButton.Custom>
              {({ account, chain, openConnectModal, mounted }) => {
                const connected = Boolean(mounted && account && chain);
                const label = getButtonLabel(connected);

                return (
                  <div className='mt-auto flex w-full flex-col gap-2'>
                    <Button
                      variant='outline'
                      className='h-12 w-full py-0 text-base font-normal'
                      disabled={connected && isActionDisabled}
                      onClick={!connected ? openConnectModal : handleAction}
                    >
                      {isProcessing && connected ? (
                        <span className='flex items-center gap-2'>
                          <AnimeLoader className='h-4 w-4 text-white' />
                          Processing...
                        </span>
                      ) : (
                        label
                      )}
                    </Button>
                  </div>
                );
              }}
            </ConnectButton.Custom>
          </>
        )}
      </div>

      {isDeposit && canSelectDepositToken && isTokenSelectDrawer && (
        <Drawer
          isOpen={isTokenSelectOpen}
          onClose={() => setIsTokenSelectOpen(false)}
          title='Select a Token'
          fitContent
          className='p-4'
        >
          <TokenSelectList
            tokens={depositTokens}
            selectedToken={selectedDepositToken.address}
            onSelect={(token) => {
              const next =
                depositTokens.find(
                  (item) => item.address.toLowerCase() === token.address.toLowerCase(),
                ) ?? depositTokens[0];
              setSelectedDepositToken(next);
              setAmount('');
              setShowSwapDetails(false);
              setIsTokenSelectOpen(false);
            }}
          />
        </Drawer>
      )}

      <Suspense fallback={null}>
        {useSequentialFlow ? (
          <SequentialStepModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={sequentialTitle}
            steps={sequentialSteps}
            errorMessage={errorMessage}
            onRetry={handleSequentialRetry}
          />
        ) : (
          <StepModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            isReversed={!isDeposit}
            transactionStatus={transactionStatus}
            onRetryApproval={() => handleSend()}
            onRetryTransaction={() => handleSend()}
            errorMessage={errorMessage}
            isBatchingTransaction={isBatchingTransaction}
            textType={StepModalTextType.TRANCHE}
            symbol={trancheTokenSymbol}
            title={isDeposit ? 'Deposit' : 'Withdraw'}
          />
        )}
        <TransactionCompleted
          isOpen={isSuccessModalOpen}
          onClose={handleCloseTransactionCompleteModal}
          txHash={txHash}
          amount={debouncedAmount}
          symbol={isDeposit ? assetSymbol : trancheTokenSymbol}
          amountOut={isDeposit ? previewSharesFormatted : previewAssetsFormatted}
          symbolOut={isDeposit ? trancheTokenSymbol : mzusdTokenSymbol}
          isReversed={!isDeposit}
          textType={getCompletedTextType(selectedTrancheId)}
        />
      </Suspense>
    </>
  );
};

export default VaultsItemActionCard;
